import { useState } from 'react';
import { Receipt, TriangleAlert } from 'lucide-react';
import {
  getApiError,
  useGetInvoiceQuery,
  useRecordPaymentMutation,
  type PaymentMethod,
} from '@/store/api';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { formatMinor, methodLabel, parseMajorToMinor, termLabel } from '@/lib/money';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  One invoice, its payment history, and the form that adds to it.

  ── Why the history and the form are the same dialog ───────────────────────

  The question a bursar is answering at the counter is "what is still owed on
  this, and what has already been paid against it" — and the second half of that
  is the part that catches a parent paying twice. Putting the receipts in a
  separate screen means the payment is recorded without ever seeing them.

  ── Why the amount defaults to the full balance ────────────────────────────

  Most payments settle the bill. Pre-filling it makes the common case one tap
  and leaves the part payment a deliberate edit, rather than making every
  payment a retyping exercise against a figure shown elsewhere on screen.
*/

const METHODS: PaymentMethod[] = ['cash', 'momo', 'bank_transfer', 'cheque', 'waiver'];

export function InvoiceDialog({
  invoiceId,
  institutionId,
  onClose,
}: {
  invoiceId: string | null;
  institutionId: string;
  onClose: () => void;
}) {
  const { data: invoice, isLoading } = useGetInvoiceQuery(
    { institutionId, invoiceId: invoiceId ?? '' },
    { skip: !invoiceId },
  );

  const [recordPayment, { isLoading: saving }] = useRecordPaymentMutation();

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('momo');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');

  /* Keyed on the invoice, so opening a different one resets the form rather
     than carrying the previous invoice's half-typed payment into it. */
  const [formFor, setFormFor] = useState<string | null>(null);
  if (invoice && formFor !== invoice._id) {
    setFormFor(invoice._id);
    setAmount(invoice.balanceMinor > 0 ? formatMinor(invoice.balanceMinor) : '');
    setMethod('momo');
    setReference('');
    setError('');
    setReceipt('');
  }

  const settled = invoice ? invoice.balanceMinor === 0 : false;

  const handleRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;
    setError('');
    setReceipt('');

    const minor = parseMajorToMinor(amount);
    if (minor === null || minor <= 0) return setError('Enter the amount received, like 250 or 250.00.');
    if (minor > invoice.balanceMinor) {
      return setError(`That is more than the GHS ${formatMinor(invoice.balanceMinor)} still owed on this invoice.`);
    }

    try {
      const result = await recordPayment({
        institutionId,
        invoiceId: invoice._id,
        data: { amountMinor: minor, method, reference: reference.trim() || undefined },
      }).unwrap();
      setReceipt(result.receiptNumber);
      setAmount('');
      setReference('');
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <Modal
      open={invoiceId !== null}
      onClose={onClose}
      size="lg"
      title={invoice ? invoice.invoiceNumber : 'Invoice'}
      description={invoice ? `${invoice.studentName} · ${invoice.groupName || 'No group'} · ${termLabel(invoice.term)} ${invoice.academicYear}` : undefined}
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {isLoading || !invoice ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* ── The three figures that matter ─────────────────────────────── */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Billed', value: invoice.totalMinor, tone: '' },
              { label: 'Paid', value: invoice.paidMinor, tone: 'text-[hsl(var(--record-ink-success))]' },
              { label: 'Balance', value: invoice.balanceMinor, tone: invoice.balanceMinor > 0 ? 'text-[hsl(var(--record-ink-danger))]' : '' },
            ].map((f) => (
              <div key={f.label} className={cn(styles.panel, 'px-3 py-2')}>
                <p className={styles.label}>{f.label}</p>
                <p className={cn(styles.figure, 'mt-0.5 text-[15px]', f.tone)}>GHS {formatMinor(f.value)}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {settled ? (
              <Mark tone="success">Settled</Mark>
            ) : invoice.isOverdue ? (
              <Mark tone="danger">Overdue</Mark>
            ) : (
              <Mark tone="warning">Outstanding</Mark>
            )}
            <span className={styles.meta}>Due {invoice.dueDate}</span>
            {invoice.parentPhone && <span className={styles.meta}>· {invoice.parentPhone}</span>}
          </div>

          {/* ── What was billed ───────────────────────────────────────────── */}
          <div className={styles.panel}>
            <p className={cn(styles.label, 'px-3 pt-2.5')}>Billed for</p>
            <ul className="px-3 pb-2.5 pt-1.5">
              {invoice.items.map((item, i) => (
                <li key={`${item.label}-${i}`} className="flex items-center justify-between py-[3px] text-[11.5px]">
                  <span className="text-foreground">{item.label}</span>
                  <span className={styles.figure}>{formatMinor(item.amountMinor)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Record a payment ──────────────────────────────────────────── */}
          {!settled && (
            <form onSubmit={handleRecord} className={cn(styles.panel, 'flex flex-col gap-2.5 p-3')}>
              <p className={styles.sectionTitle}>Record a payment</p>

              {error && (
                <div className={cn(styles.markDanger, 'w-full justify-start normal-case tracking-normal text-[11px]')}>
                  <TriangleAlert size={12} className="flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="grid gap-2.5 sm:grid-cols-3">
                <Field label="Amount" required>
                  <Input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                    className="text-right tabular-nums"
                  />
                </Field>
                <Field label="Method" required>
                  <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                    {METHODS.map((m) => (
                      <option key={m} value={m}>
                        {methodLabel(m)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Reference" hint="Momo or bank ref">
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Optional"
                  />
                </Field>
              </div>

              <div className="flex justify-end">
                <Button type="submit" loading={saving}>
                  Record payment
                </Button>
              </div>
            </form>
          )}

          {receipt && (
            <div className={cn(styles.markSuccess, 'w-full justify-start normal-case tracking-normal text-[11px]')}>
              <Receipt size={12} className="flex-shrink-0" />
              Recorded — receipt {receipt}
            </div>
          )}

          {/* ── The cash record ───────────────────────────────────────────── */}
          <div>
            <p className={cn(styles.label, 'mb-1.5')}>Payments</p>
            {invoice.payments.length === 0 ? (
              <p className={styles.meta}>Nothing received against this invoice yet.</p>
            ) : (
              <div className={cn(styles.panel, 'divide-y divide-border')}>
                {invoice.payments.map((p) => (
                  <div key={p._id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-[11.5px] font-semibold text-foreground">
                        {methodLabel(p.method)}
                        {p.reference && <span className="font-normal text-muted-foreground"> · {p.reference}</span>}
                      </p>
                      <p className={styles.meta}>
                        {p.receiptNumber} · {new Date(p.receivedAt).toLocaleString('en-GH')}
                      </p>
                    </div>
                    <span className={cn(styles.figure, 'flex-shrink-0 text-[12.5px]')}>
                      {formatMinor(p.amountMinor)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
